/**
 * Time Clock Service - Manages employee time tracking with Firebase persistence
 * 
 * Database Structure: companies/{companyId}/users/{userId}/timeclock/{entryId}
 * 
 * Features:
 * - Clock in/out with GPS verification
 * - Break tracking
 * - Overtime calculation
 * - Manager approval workflow
 * - Payroll estimates
 * - Shift reminders
 */

import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    getDocs,
    getDoc,
    setDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    onSnapshot,
    writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import { getUsersByCompany } from '../auth/userService';

// ============================================================================
// TYPES
// ============================================================================

export interface TimeEntry {
    id: string;
    odId: string;
    companyId: string;
    clockIn: string; // ISO timestamp
    clockInLocation: LocationData;
    clockOut?: string; // ISO timestamp
    clockOutLocation?: LocationData;
    hoursWorked?: number;
    regularHours?: number;
    overtimeHours?: number;
    mileage?: number;
    notes?: string;
    wellsVisited: string[];
    breaks: Break[];
    status: 'active' | 'completed' | 'pending_approval' | 'approved' | 'rejected' | 'edited';
    approvedBy?: string;
    approvedAt?: string;
    editedBy?: string;
    editedAt?: string;
    editReason?: string;
    originalEntry?: Partial<TimeEntry>; // For audit trail when edited
    createdAt: string;
    updatedAt: string;
}

export interface LocationData {
    lat: number;
    lng: number;
    address: string;
    accuracy?: number;
}

export interface Break {
    id: string;
    startTime: string;
    endTime?: string;
    type: 'lunch' | 'short' | 'other';
    paid: boolean;
    duration?: number; // in minutes
}

export interface WorkSchedule {
    userId: string;
    companyId: string;
    dayOfWeek: number; // 0-6 (Sunday-Saturday)
    startTime: string; // "08:00"
    endTime: string; // "17:00"
    isActive: boolean;
}

export interface PayrollSettings {
    userId: string;
    companyId: string;
    hourlyRate: number;
    overtimeRate: number; // multiplier (e.g., 1.5)
    overtimeThreshold: number; // hours per week (e.g., 40)
    mileageRate: number; // per mile
    payPeriod: 'weekly' | 'biweekly' | 'monthly';
    payPeriodStart: number; // day of week/month
}

export interface TimesheetSummary {
    userId: string;
    userName: string;
    userEmail?: string;
    userRole?: string;
    periodStart: string;
    periodEnd: string;
    totalHours: number;
    regularHours: number;
    overtimeHours: number;
    totalMileage: number;
    totalBreakTime: number;
    entriesCount: number;
    pendingApproval: number;
    approved: number;
    estimatedPay: number;
    entries: TimeEntry[];
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getTimeClockCollectionPath(companyId: string, userId: string): string {
    return `companies/${companyId}/users/${userId}/timeclock`;
}

function getScheduleCollectionPath(companyId: string, userId: string): string {
    return `companies/${companyId}/users/${userId}/workSchedule`;
}

function getPayrollSettingsPath(companyId: string, userId: string): string {
    return `companies/${companyId}/users/${userId}/payrollSettings`;
}

function calculateHoursWorked(clockIn: string, clockOut: string, breaks: Break[] = []): number {
    const start = new Date(clockIn).getTime();
    const end = new Date(clockOut).getTime();
    const totalMs = end - start;

    // Subtract break time
    const breakMs = breaks.reduce((total, brk) => {
        if (brk.startTime && brk.endTime && !brk.paid) {
            return total + (new Date(brk.endTime).getTime() - new Date(brk.startTime).getTime());
        }
        return total;
    }, 0);

    return Math.max(0, (totalMs - breakMs) / (1000 * 60 * 60)); // Convert to hours
}

function calculateOvertime(totalHours: number, threshold: number = 40): { regular: number; overtime: number } {
    if (totalHours <= threshold) {
        return { regular: totalHours, overtime: 0 };
    }
    return { regular: threshold, overtime: totalHours - threshold };
}

// ============================================================================
// TIME ENTRY OPERATIONS
// ============================================================================

/**
 * Get the clockins collection path for manager viewing
 * Format: companies/{companyId}/users/{userId}/clockins
 */
function getClockInsCollectionPath(companyId: string, userId: string): string {
    return `companies/${companyId}/users/${userId}/clockins`;
}

/**
 * Clock in a user
 */
export async function clockIn(
    userId: string,
    location: LocationData,
    notes?: string
): Promise<TimeEntry> {
    const companyId = getCompanyId();
    const now = new Date();
    const nowIso = now.toISOString();
    const dateKey = now.toISOString().split('T')[0]; // YYYY-MM-DD format

    const entry: Omit<TimeEntry, 'id'> = {
        odId: userId,
        companyId,
        clockIn: nowIso,
        clockInLocation: location,
        wellsVisited: [],
        breaks: [],
        status: 'active',
        createdAt: nowIso,
        updatedAt: nowIso,
        notes: notes || ''
    };

    const collectionRef = collection(db, getTimeClockCollectionPath(companyId, userId));
    const docRef = await addDoc(collectionRef, entry);

    // Also save to clockins collection for manager tracking (hidden from pumpers)
    // Path: companies/{companyId}/users/{userId}/clockins/{isoDate}
    try {
        const clockInDocRef = doc(db, getClockInsCollectionPath(companyId, userId), dateKey);
        await setDoc(clockInDocRef, {
            userId,
            companyId,
            date: dateKey,
            clockInTime: nowIso,
            clockInLocation: location,
            entryId: docRef.id,
            updatedAt: nowIso
        }, { merge: true });
    } catch (err) {
        // Don't fail clock-in if manager tracking fails
        console.error('Failed to save clock-in location for managers:', err);
    }

    return { ...entry, id: docRef.id };
}

/**
 * Clock out a user
 */
export async function clockOut(
    userId: string,
    entryId: string,
    location: LocationData,
    mileage?: number,
    notes?: string
): Promise<TimeEntry> {
    const companyId = getCompanyId();
    const now = new Date();
    const nowIso = now.toISOString();

    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        throw new Error('Time entry not found');
    }

    const existingEntry = { id: docSnap.id, ...docSnap.data() } as TimeEntry;

    // End any active breaks
    const updatedBreaks = existingEntry.breaks.map(brk => {
        if (!brk.endTime) {
            return { ...brk, endTime: nowIso, duration: calculateBreakDuration(brk.startTime, nowIso) };
        }
        return brk;
    });

    const hoursWorked = calculateHoursWorked(existingEntry.clockIn, nowIso, updatedBreaks);

    const updates: Partial<TimeEntry> = {
        clockOut: nowIso,
        clockOutLocation: location,
        hoursWorked: parseFloat(hoursWorked.toFixed(2)),
        mileage: mileage || 0,
        notes: notes || existingEntry.notes,
        breaks: updatedBreaks,
        status: 'pending_approval',
        updatedAt: nowIso
    };

    await updateDoc(docRef, updates);

    // Also update clockins collection for manager tracking (hidden from pumpers)
    // Update the clock-out info for the date the shift started
    try {
        const clockInDate = new Date(existingEntry.clockIn);
        const dateKey = clockInDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        const clockInDocRef = doc(db, getClockInsCollectionPath(companyId, userId), dateKey);
        await setDoc(clockInDocRef, {
            clockOutTime: nowIso,
            clockOutLocation: location,
            hoursWorked: parseFloat(hoursWorked.toFixed(2)),
            mileage: mileage || 0,
            updatedAt: nowIso
        }, { merge: true });
    } catch (err) {
        // Don't fail clock-out if manager tracking fails
        console.error('Failed to update clock-out location for managers:', err);
    }

    return { ...existingEntry, ...updates };
}

function calculateBreakDuration(start: string, end: string): number {
    return Math.round((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60));
}

/**
 * Start a break
 */
export async function startBreak(
    userId: string,
    entryId: string,
    breakType: Break['type'] = 'short',
    paid: boolean = false
): Promise<void> {
    const companyId = getCompanyId();
    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        throw new Error('Time entry not found');
    }

    const entry = docSnap.data() as TimeEntry;
    const newBreak: Break = {
        id: `break-${Date.now()}`,
        startTime: new Date().toISOString(),
        type: breakType,
        paid
    };

    await updateDoc(docRef, {
        breaks: [...entry.breaks, newBreak],
        updatedAt: new Date().toISOString()
    });
}

/**
 * End a break
 */
export async function endBreak(
    userId: string,
    entryId: string
): Promise<void> {
    const companyId = getCompanyId();
    const now = new Date().toISOString();
    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        throw new Error('Time entry not found');
    }

    const entry = docSnap.data() as TimeEntry;
    const updatedBreaks = entry.breaks.map(brk => {
        if (!brk.endTime) {
            return { ...brk, endTime: now, duration: calculateBreakDuration(brk.startTime, now) };
        }
        return brk;
    });

    await updateDoc(docRef, {
        breaks: updatedBreaks,
        updatedAt: now
    });
}

/**
 * Get user's time entries for a date range
 */
export async function getUserTimeEntries(
    userId: string,
    startDate?: Date,
    endDate?: Date
): Promise<TimeEntry[]> {
    const companyId = getCompanyId();
    const collectionPath = getTimeClockCollectionPath(companyId, userId);
    const collectionRef = collection(db, collectionPath);
    

    try {
        // Build query with proper Firestore constraint ordering
        // When using range filters (>=, <=), they must be on the orderBy field
        const constraints: any[] = [];
        
        if (startDate && endDate) {
            // Both dates: use range query then sort
            constraints.push(where('clockIn', '>=', startDate.toISOString()));
            constraints.push(where('clockIn', '<=', endDate.toISOString()));
            constraints.push(orderBy('clockIn', 'desc'));
        } else if (startDate) {
            constraints.push(where('clockIn', '>=', startDate.toISOString()));
            constraints.push(orderBy('clockIn', 'desc'));
        } else if (endDate) {
            constraints.push(where('clockIn', '<=', endDate.toISOString()));
            constraints.push(orderBy('clockIn', 'desc'));
        } else {
            constraints.push(orderBy('clockIn', 'desc'));
        }

        const q = query(collectionRef, ...constraints);
        const snapshot = await getDocs(q);
        
        
        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TimeEntry));
    } catch (error) {
        console.error('❌ Error fetching time entries:', error);
        // If the query fails (e.g., missing index), try without date filters
        try {
            const simpleQuery = query(collectionRef, orderBy('clockIn', 'desc'));
            const snapshot = await getDocs(simpleQuery);
            
            // Filter in memory if date filters were requested
            let entries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TimeEntry));
            
            if (startDate) {
                entries = entries.filter(e => new Date(e.clockIn) >= startDate);
            }
            if (endDate) {
                entries = entries.filter(e => new Date(e.clockIn) <= endDate);
            }
            
            return entries;
        } catch (fallbackError) {
            console.error('❌ Fallback query also failed:', fallbackError);
            return [];
        }
    }
}

/**
 * Get current active entry (if clocked in)
 */
export async function getActiveEntry(userId: string): Promise<TimeEntry | null> {
    const companyId = getCompanyId();
    const collectionRef = collection(db, getTimeClockCollectionPath(companyId, userId));
    const q = query(collectionRef, where('status', '==', 'active'), limit(1));

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as TimeEntry;
}

/**
 * Subscribe to user's clock status
 */
export function subscribeToClockStatus(
    userId: string,
    callback: (entry: TimeEntry | null) => void
): () => void {
    const companyId = getCompanyId();
    const collectionRef = collection(db, getTimeClockCollectionPath(companyId, userId));
    const q = query(collectionRef, where('status', '==', 'active'), limit(1));

    return onSnapshot(q, (snapshot) => {
        if (snapshot.empty) {
            callback(null);
        } else {
            callback({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as TimeEntry);
        }
    });
}

// ============================================================================
// MANAGER OPERATIONS
// ============================================================================

/**
 * Get all employees' time entries for a company (manager only)
 */
export async function getAllEmployeeEntries(
    startDate?: Date,
    endDate?: Date,
    status?: TimeEntry['status']
): Promise<Map<string, TimeEntry[]>> {
    const companyId = getCompanyId();

    // Get all users in the company
    const usersRef = collection(db, `companies/${companyId}/users`);
    const usersSnapshot = await getDocs(usersRef);

    const entriesByUser = new Map<string, TimeEntry[]>();

    for (const userDoc of usersSnapshot.docs) {
        const userId = userDoc.id;
        const entries = await getUserTimeEntries(userId, startDate, endDate);

        const filteredEntries = status
            ? entries.filter(e => e.status === status)
            : entries;

        if (filteredEntries.length > 0) {
            entriesByUser.set(userId, filteredEntries);
        }
    }

    return entriesByUser;
}

/**
 * Get timesheet summaries for all employees (manager view)
 */
export async function getTimesheetSummaries(
    startDate: Date,
    endDate: Date
): Promise<TimesheetSummary[]> {
    const companyId = getCompanyId();

    // Use the standard getUsersByCompany to get all users for this tenant
    let users: any[] = [];
    
    try {
        users = await getUsersByCompany({ isActive: true });
    } catch (error) {
        console.error('❌ Error fetching users via getUsersByCompany:', error);
        
        // Fallback: Try direct Firestore query without companyId filter
        try {
            const usersRef = collection(db, `companies/${companyId}/users`);
            const snapshot = await getDocs(usersRef);
            users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (fallbackError) {
            console.error('❌ Fallback query also failed:', fallbackError);
        }
    }

    if (users.length === 0) {
        return [];
    }

    const summaries: TimesheetSummary[] = [];

    for (const user of users) {
        const userId = user.id;
        const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown';
        
        
        const entries = await getUserTimeEntries(userId, startDate, endDate);

        if (entries.length === 0) {
            continue;
        }
        

        const completedEntries = entries.filter(e => e.clockOut);
        const totalHours = completedEntries.reduce((sum, e) => sum + (e.hoursWorked || 0), 0);
        const totalMileage = completedEntries.reduce((sum, e) => sum + (e.mileage || 0), 0);
        const totalBreakTime = completedEntries.reduce((sum, e) => {
            return sum + e.breaks.reduce((bSum, brk) => bSum + (brk.duration || 0), 0);
        }, 0);

        const { regular, overtime } = calculateOvertime(totalHours);

        // Get payroll settings for estimate
        const payroll = await getPayrollSettings(userId);
        const estimatedPay = payroll
            ? (regular * payroll.hourlyRate) + (overtime * payroll.hourlyRate * payroll.overtimeRate) + (totalMileage * payroll.mileageRate)
            : totalHours * 25; // Default $25/hr

        summaries.push({
            userId,
            userName,
            userEmail: user.email,
            userRole: user.role,
            periodStart: startDate.toISOString(),
            periodEnd: endDate.toISOString(),
            totalHours,
            regularHours: regular,
            overtimeHours: overtime,
            totalMileage,
            totalBreakTime,
            entriesCount: completedEntries.length,
            pendingApproval: entries.filter(e => e.status === 'pending_approval').length,
            approved: entries.filter(e => e.status === 'approved').length,
            estimatedPay,
            entries
        });
    }

    return summaries.sort((a, b) => b.totalHours - a.totalHours);
}

/**
 * Approve a time entry (manager only)
 */
export async function approveTimeEntry(
    userId: string,
    entryId: string,
    managerId: string
): Promise<void> {
    const companyId = getCompanyId();
    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);

    await updateDoc(docRef, {
        status: 'approved',
        approvedBy: managerId,
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

}

/**
 * Reject a time entry (manager only)
 */
export async function rejectTimeEntry(
    userId: string,
    entryId: string,
    managerId: string,
    reason: string
): Promise<void> {
    const companyId = getCompanyId();
    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);

    await updateDoc(docRef, {
        status: 'rejected',
        approvedBy: managerId,
        approvedAt: new Date().toISOString(),
        editReason: reason,
        updatedAt: new Date().toISOString()
    });
}

/**
 * Edit a time entry (manager only)
 */
export async function editTimeEntry(
    userId: string,
    entryId: string,
    managerId: string,
    updates: {
        clockIn?: string;
        clockOut?: string;
        mileage?: number;
        notes?: string;
    },
    reason: string
): Promise<void> {
    const companyId = getCompanyId();
    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        throw new Error('Time entry not found');
    }

    const existingEntry = docSnap.data() as TimeEntry;

    // Calculate new hours if times changed
    let hoursWorked = existingEntry.hoursWorked;
    if (updates.clockIn || updates.clockOut) {
        const clockIn = updates.clockIn || existingEntry.clockIn;
        const clockOut = updates.clockOut || existingEntry.clockOut;
        if (clockOut) {
            hoursWorked = calculateHoursWorked(clockIn, clockOut, existingEntry.breaks);
        }
    }

    await updateDoc(docRef, {
        ...updates,
        hoursWorked: hoursWorked ? parseFloat(hoursWorked.toFixed(2)) : undefined,
        status: 'edited',
        editedBy: managerId,
        editedAt: new Date().toISOString(),
        editReason: reason,
        originalEntry: {
            clockIn: existingEntry.clockIn,
            clockOut: existingEntry.clockOut,
            hoursWorked: existingEntry.hoursWorked,
            mileage: existingEntry.mileage
        },
        updatedAt: new Date().toISOString()
    });

}

/**
 * Bulk approve entries (manager only)
 */
export async function bulkApproveEntries(
    entries: { userId: string; entryId: string }[],
    managerId: string
): Promise<void> {
    const companyId = getCompanyId();
    const batch = writeBatch(db);
    const now = new Date().toISOString();

    for (const { userId, entryId } of entries) {
        const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);
        batch.update(docRef, {
            status: 'approved',
            approvedBy: managerId,
            approvedAt: now,
            updatedAt: now
        });
    }

    await batch.commit();
}

// ============================================================================
// PAYROLL & SCHEDULE SETTINGS
// ============================================================================

/**
 * Get user's payroll settings
 */
export async function getPayrollSettings(userId: string): Promise<PayrollSettings | null> {
    const companyId = getCompanyId();
    const docRef = doc(db, getPayrollSettingsPath(companyId, userId), 'settings');
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;
    return docSnap.data() as PayrollSettings;
}

/**
 * Save user's payroll settings
 */
export async function savePayrollSettings(
    userId: string,
    settings: Omit<PayrollSettings, 'userId' | 'companyId'>
): Promise<void> {
    const companyId = getCompanyId();
    const docRef = doc(db, getPayrollSettingsPath(companyId, userId), 'settings');

    await updateDoc(docRef, {
        ...settings,
        userId,
        companyId,
        updatedAt: new Date().toISOString()
    }).catch(() => {
        // Document doesn't exist, create it
        return addDoc(collection(db, getPayrollSettingsPath(companyId, userId)), {
            ...settings,
            userId,
            companyId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
    });
}

/**
 * Get user's work schedule
 */
export async function getWorkSchedule(userId: string): Promise<WorkSchedule[]> {
    const companyId = getCompanyId();
    const collectionRef = collection(db, getScheduleCollectionPath(companyId, userId));
    const snapshot = await getDocs(collectionRef);

    return snapshot.docs.map(doc => doc.data() as WorkSchedule);
}

/**
 * Save user's work schedule
 */
export async function saveWorkSchedule(
    userId: string,
    schedules: Omit<WorkSchedule, 'userId' | 'companyId'>[]
): Promise<void> {
    const companyId = getCompanyId();
    const batch = writeBatch(db);

    // Delete existing schedules
    const collectionRef = collection(db, getScheduleCollectionPath(companyId, userId));
    const existing = await getDocs(collectionRef);
    existing.forEach(doc => batch.delete(doc.ref));

    // Add new schedules
    for (const schedule of schedules) {
        const newDocRef = doc(collectionRef);
        batch.set(newDocRef, {
            ...schedule,
            userId,
            companyId
        });
    }

    await batch.commit();
}

// ============================================================================
// REMINDER FUNCTIONS
// ============================================================================

/**
 * Check if user should be reminded to clock in
 */
export async function shouldRemindClockIn(userId: string): Promise<boolean> {
    const schedule = await getWorkSchedule(userId);
    const activeEntry = await getActiveEntry(userId);

    if (activeEntry) return false; // Already clocked in

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    const todaySchedule = schedule.find(s => s.dayOfWeek === currentDay && s.isActive);
    if (!todaySchedule) return false;

    // Remind 5 minutes after scheduled start time
    const [scheduleHour, scheduleMin] = todaySchedule.startTime.split(':').map(Number);
    const scheduleStartMs = (scheduleHour * 60 + scheduleMin) * 60 * 1000;
    const currentMs = (now.getHours() * 60 + now.getMinutes()) * 60 * 1000;

    // Within 30 minutes of start time and past start time
    return currentMs > scheduleStartMs && currentMs < scheduleStartMs + (30 * 60 * 1000);
}

/**
 * Check if user should be reminded to clock out
 */
export async function shouldRemindClockOut(userId: string): Promise<boolean> {
    const schedule = await getWorkSchedule(userId);
    const activeEntry = await getActiveEntry(userId);

    if (!activeEntry) return false; // Not clocked in

    const now = new Date();
    const currentDay = now.getDay();

    const todaySchedule = schedule.find(s => s.dayOfWeek === currentDay && s.isActive);
    if (!todaySchedule) {
        // If no schedule, remind after 10 hours
        const hoursWorked = (now.getTime() - new Date(activeEntry.clockIn).getTime()) / (1000 * 60 * 60);
        return hoursWorked >= 10;
    }

    const [scheduleHour, scheduleMin] = todaySchedule.endTime.split(':').map(Number);
    const scheduleEndMs = (scheduleHour * 60 + scheduleMin) * 60 * 1000;
    const currentMs = (now.getHours() * 60 + now.getMinutes()) * 60 * 1000;

    // 5 minutes past end time
    return currentMs > scheduleEndMs + (5 * 60 * 1000);
}

/**
 * Delete a time entry (manager only)
 */
export async function deleteTimeEntry(userId: string, entryId: string): Promise<void> {
    const companyId = getCompanyId();
    const docRef = doc(db, getTimeClockCollectionPath(companyId, userId), entryId);
    await deleteDoc(docRef);
}

export const timeClockService = {
    clockIn,
    clockOut,
    startBreak,
    endBreak,
    getUserTimeEntries,
    getActiveEntry,
    subscribeToClockStatus,
    getAllEmployeeEntries,
    getTimesheetSummaries,
    approveTimeEntry,
    rejectTimeEntry,
    editTimeEntry,
    bulkApproveEntries,
    deleteTimeEntry,
    getPayrollSettings,
    savePayrollSettings,
    getWorkSchedule,
    saveWorkSchedule,
    shouldRemindClockIn,
    shouldRemindClockOut
};

export default timeClockService;
